import React from 'react';
import ReactHintFactory from 'react-hint';
import { Button, CashBack, MobilePopupBottom } from '@lt/components';

import styled from 'styled-components';
import { i18n } from '@lt/localization';
import CurrencyFormat from '../../../../../../../components/CurrencyFormat';
import PriceChecker from '../../../../../components/PriceCheckerOld';
import BillInfoSkeleton from '../../../../../components/PackageInfo/BillInfoSkeleton';
import BestPriceTooltip from '../BestPriceTooltip';
import PriceNotice from '../../PriceNotice';
import PriceNoticeHint from '../../PriceNotice/PriceNoticeHint';
import ReactHint from '../../../containers/Hint/HintFactory';

import Icon from '../../../../../../../components/Icon';
import {
  PriceDetails,
  PriceDetailsItem,
} from '../../../../../components/PriceDetails';
import { EXTRAS, EXTRA_SCOPES } from '../../../../../store/constants/extras';

import type { IBillInfo } from './types';
import type { ExtraItem } from '../../../../../types/extras';
import { HotPrice } from '../../../../../components/HotPrice';
import {
  calculateCashback,
  limitClientCashbackDiscount,
  shouldApplyClientCashback,
} from '../../../../../utils/orderUtils';

const ReactHintDesktop = ReactHintFactory(React);
const baseCls = 'checkout-billinfo';

interface IState {
  isTooltipOpened: boolean;
}
class BillInfo extends React.PureComponent<IBillInfo, IState> {
  private priceRef: React.RefObject<HTMLDivElement>;

  constructor(props) {
    super(props);

    this.handleBestPriceClicked = this.handleBestPriceClicked.bind(this);
    this.priceRef = React.createRef();
    this.state = { isTooltipOpened: false };
  }

  componentDidMount() {
    this.togglePriceHint();
  }

  componentDidUpdate(prevProps) {
    const { showPriceChanged } = this.props;

    if (showPriceChanged !== prevProps.showPriceChanged) {
      this.togglePriceHint();
    }
  }

  handleBestPriceClicked = () => {
    const { isMobile } = this.props;
    const { isTooltipOpened } = this.state;
    if (!isMobile) return;
    this.setState({ isTooltipOpened: !isTooltipOpened });
  };

  /**
   * Тогллит хинт изменения цены
   */
  togglePriceHint() {
    if (!PriceNoticeHint || !PriceNoticeHint.hintInstance) {
      return;
    }

    const { showPriceChanged } = this.props;

    PriceNoticeHint.hintInstance.toggleHint({
      target: showPriceChanged ? this.priceRef.current : null,
    });
  }

  render() {
    const {
      fetching,
      minimalFuelCharge,
      netPrice,
      mandatoryExtras,
      closePriceNotice,
      instantConfirm,
      isManualPackage,
      skipFlights,
      isMobile,
      oldPrice,
      discount,
      isArchived,
      bonuses,
      customCashbackIconURL,
      customCashbackText,
      customCashbackTooltipText,
      activateCashbackForPartner,
      isWhiteLabel,
      isLoggedIn,
      clientCashback,
    } = this.props;

    const extraPrice = mandatoryExtras.reduce(
      (extrasPrice, extra) => extrasPrice + (extra.price || 0),
      0,
    );

    const fullPrice = netPrice + minimalFuelCharge + extraPrice;

    const hasAppliedClientCashback = shouldApplyClientCashback(
      isWhiteLabel,
      isLoggedIn,
      clientCashback,
    );

    const applicableClientCashbackDiscount = hasAppliedClientCashback
      ? limitClientCashbackDiscount(clientCashback, fullPrice)
      : 0;

    const finalPrice = fullPrice - applicableClientCashbackDiscount;

    const shouldGetCashback = activateCashbackForPartner && !isArchived;

    const cashbackFromPurchase = shouldGetCashback
      ? calculateCashback(bonuses, finalPrice)
      : 0;

    const extras = [
      { name: i18n.t('checkout.billInfo.extras'), price: minimalFuelCharge },
      /** Фильтруем услугу конкретизации рейса, так как она не добавляется к самому дешевому рейсу */
      ...mandatoryExtras.filter(
        (extra) => extra.code_name !== EXTRAS.FLIGHT_LOCK,
      ),
    ] as ExtraItem[];

    const tooltipProps = isMobile
      ? {}
      : {
          'data-warranty': true,
        };

    const { isTooltipOpened } = this.state;

    return (
      <StyledContainer className={baseCls} $isArchived={isArchived}>
        <MobilePopupBottom
          onClick={() => {
            this.setState({ isTooltipOpened: false });
          }}
          visible={isTooltipOpened}
          options={{ showHandlebar: true }}
        >
          <BestPriceTooltip isMobile />
        </MobilePopupBottom>
        {fetching ? (
          <BillInfoSkeleton />
        ) : (
          <div className={`${baseCls}__block`}>
            <StyledPriceNoticeHint
              className="react-hint tooltip-warning react-hint__price-changed"
              attribute="data-price-changed"
              events={{}}
              ref={(ref) => {
                PriceNoticeHint.hintInstance = ref;
              }}
              onRenderContent={() => (
                <PriceNoticeHint onClose={closePriceNotice}>
                  <PriceNotice />
                </PriceNoticeHint>
              )}
            />
            <ReactHint
              events={{ click: true }}
              attribute="data-warranty"
              className="tooltip-payment react-hint"
              onRenderContent={() =>
                !isMobile && <BestPriceTooltip isMobile={false} />
              }
            />
            <PriceHeading
              className={`${baseCls}__title`}
              $isArchived={isArchived}
            >
              {i18n.t('checkout.billInfo.bestPrice')}
              <button
                type="button"
                className={`${baseCls}__button`}
                onClick={this.handleBestPriceClicked}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...tooltipProps}
              >
                ?
              </button>
            </PriceHeading>
            <PriceBlock $isMobile={isMobile}>
              <h3
                ref={this.priceRef}
                className={`${baseCls}__total like-h2`}
                data-price-changed
              >
                {instantConfirm && <Icon type="lightning" />}
                <CurrencyFormat
                  className={`${baseCls}__value`}
                  value={finalPrice}
                />
                {isMobile && <HotPrice value={oldPrice} discount={discount} />}
              </h3>
              {shouldGetCashback && (
                <StyledCashBack
                  customCashbackIconURL={customCashbackIconURL}
                  customCashbackText={customCashbackText}
                  customCashbackTooltipText={customCashbackTooltipText}
                  count={`+${cashbackFromPurchase}`}
                  cashbackText
                  $isMobile={isMobile}
                  type={bonuses?.cashback_identifier}
                />
              )}
            </PriceBlock>
            <div className={`${baseCls}__summarize`}>
              <PriceDetails>
                <PriceDetailsItem
                  className={`${baseCls}__base-price`}
                  label={i18n.t('checkout.billInfo.baseCost_package')}
                  isArchived={isArchived}
                >
                  {!isMobile && (
                    <HotPrice value={oldPrice} discount={discount} />
                  )}
                  <CurrencyFormat value={netPrice} />
                </PriceDetailsItem>
                {extras.map((extra) => {
                  const { name, price, scope, mandatory } = extra;
                  const showAsMandatory =
                    mandatory && scope !== EXTRA_SCOPES.LEVEL_TRAVEL;
                  const label = showAsMandatory
                    ? `${name} (${i18n.t('checkout.billInfo.mandatory')})`
                    : name;
                  return (
                    <PriceDetailsItem
                      key={name}
                      label={label}
                      price={price}
                      highlighted
                      isArchived={isArchived}
                    />
                  );
                })}
              </PriceDetails>
              {applicableClientCashbackDiscount > 0 && (
                <PriceDetails>
                  <PriceDetailsItem
                    label={i18n.t('checkout.discount.priceReducedByCashback')}
                    price={applicableClientCashbackDiscount}
                    isReduction
                    highlighted
                  />
                </PriceDetails>
              )}
              {!isArchived && (
                <div className={`${baseCls}__counter`}>
                  <span>{`${
                    !isMobile ? `${i18n.t('checkout.billInfo.counter')}!` : ''
                  } ${i18n.t('checkout.billInfo.counter')}`}</span>
                  <PriceChecker
                    text={i18n.t('checkout.billInfo.priceChecker', {
                      context: isMobile ? 'mobile' : 'desktop',
                    })}
                  />
                </div>
              )}
            </div>

            {!isArchived && isManualPackage && (
              <Button
                className={`${baseCls}__continue_without_flight`}
                onClick={skipFlights}
                size="normal"
                primary
              >
                {i18n.t('checkout.billInfo.withoutFlight')}
              </Button>
            )}
          </div>
        )}
      </StyledContainer>
    );
  }
}

const StyledPriceNoticeHint = styled(ReactHintDesktop)`
  z-index: 9;
`;

const StyledContainer = styled.div<{ $isArchived: boolean }>`
  ${({ $isArchived }) => $isArchived && 'background: #F2F3F4;'}
`;

const PriceHeading = styled.h2<{ $isArchived: boolean }>`
  ${({ $isArchived }) => $isArchived && 'color: #919499;'}
`;

const PriceBlock = styled.div<{ $isMobile: boolean }>`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: ${({ $isMobile }) => ($isMobile ? '9px' : '7px')};
  gap: ${({ $isMobile }) => ($isMobile ? '3px' : 0)};
`;

const StyledCashBack = styled(CashBack)<{ $isMobile: boolean }>`
  flex-basis: ${({ $isMobile }) => ($isMobile ? '100%' : 'auto')};
`;

export default BillInfo;
